import React, { useMemo } from 'react';
import './order-card.scss';
import moment from 'moment';
import { SVGS } from '../../assets';
import { Divider, KeyValuePair, StatusBadge, TagChip } from '../../components';
import { TouchableOpacity, Text } from '../../common';
import {
  OrderDataResponse,
  useAppSelector,
  getSelectedItems,
  dispatch,
  updateSelectedItemsMap,
} from '@zonofi/zono-money-store';
import { getCalculatedAmount } from '@zonofi/common/functions/format-amount';
import { DividerColorVariant, OrderStatusEnum } from '../../constants';
import { getOrderStatusStyle, getOrderStatusText } from '../../helper';
import { ReactComponent as Checkbox } from '@zonofi/svg-icons/checkbox.svg';
import { ReactComponent as Checked } from '@zonofi/svg-icons/checked.svg';

const TAGS_SHOW_COUNT = 3;

interface OrderCardProps {
  item: OrderDataResponse;
  onOrderItemPress?: (item: OrderDataResponse) => void;
  showSelection?: boolean;
}

export const OrderCard: React.FC<OrderCardProps> = ({
  item,
  onOrderItemPress,
  showSelection = false,
}) => {
  const selectedOrderItems = useAppSelector(getSelectedItems);
  const isSelected = selectedOrderItems.some(
    (selected) => selected.id === item.id
  );

  const extraTagsCount = useMemo(() => {
    return item?.tags?.length - TAGS_SHOW_COUNT;
  }, [item?.tags?.length]);

  const orderValueInfo = useMemo(
    () => [
      {
        id: 0,
        title: 'Ordered Date',
        value: item?.documentDate
          ? moment(item.documentDate).format('MMM DD, YYYY')
          : 'N/A',
      },
      {
        id: 1,
        title: 'SKU Count',
        value: item?.skucount?.toString() ?? 'N/A',
      },
      {
        id: 2,
        title: 'Order Amount',
        value: getCalculatedAmount(item?.totalWithTax || '0') ?? 'N/A',
      },
    ],
    [item?.documentDate, item?.skucount, item?.totalWithTax]
  );

  return (
    <TouchableOpacity
      className="order-card"
      onPress={() => onOrderItemPress?.(item)}
      disabled={showSelection && item?.status === OrderStatusEnum.ADDING_ITEMS}
    >
      <div className="order-card-content">
        <div className="order-card-header">
          <div className="order-card-header-lefview">
            {showSelection &&
              (isSelected ? (
                <Checked
                  className="invoice-card-content-header-top-left-checkbox"
                  color={'#000000'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(updateSelectedItemsMap(item));
                  }}
                />
              ) : (
                <Checkbox
                  className="invoice-card-content-header-top-left-checkbox"
                  color={'#8C8C8C'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(updateSelectedItemsMap(item));
                  }}
                />
              ))}
            <img src={SVGS.OrderWhatsAppSource} alt="" />
            <div className="order-card-customer-details">
              <Text className="order-card-reference-number font-medium">
                {`#${item?.orderMetaData?.refOrderNumber || 'N.A'}`}
              </Text>
              <Text className="order-card-customer-name font-medium">
                {`By ${
                  item?.customerFirmName ||
                  item?.customerName ||
                  '<Not Available>'
                }`}
              </Text>
            </div>
          </div>

          <StatusBadge
            status={getOrderStatusText(item?.status ?? '')}
            style={getOrderStatusStyle(item?.status ?? '')}
          />
        </div>

        <Divider
          colorVariant={DividerColorVariant.PRIMARY}
          className="divider-view"
        />

        <div className="order-card-values-card">
          {orderValueInfo.map((info) => (
            <KeyValuePair key={info.id} title={info.title} value={info.value} />
          ))}
        </div>

        {item?.tags?.length > 0 && (
          <div className="order-card-tags-view">
            {item.tags.slice(0, TAGS_SHOW_COUNT).map((tag: any) => (
              <TagChip key={tag?.id} tagName={tag?.name} />
            ))}
            {extraTagsCount > 0 && <TagChip tagName={`+${extraTagsCount}`} />}
          </div>
        )}
      </div>
    </TouchableOpacity>
  );
};
